import React, { useEffect, useId, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  buildReferralTrend,
  daysInMonth,
  defaultMonths,
  formatMonthLabel,
  MAX_DAYS,
  monthInputValue,
  monthValidationMessage,
  parseMonthInput,
  singaporeDateKey,
  TrendCaseInput,
  TrendDay,
} from '@/lib/doctorReferralTrend';

const TREND_CSS = `
.doctor-referral-trend {
  color-scheme: light;
  --surface-1: #fcfcfb;
  --text-primary: #0b0b0b;
  --text-secondary: #52514e;
  --text-muted: #898781;
  --gridline: #e1e0d9;
  --baseline: #c3c2b7;
  --series-1: #2a78d6;
  --series-2: #eb6834;
}
.dark .doctor-referral-trend {
  color-scheme: dark;
  --surface-1: #1a1a19;
  --text-primary: #f0efec;
  --text-secondary: #c3c2b7;
  --text-muted: #898781;
  --gridline: #2c2c2a;
  --baseline: #383835;
  --series-1: #3987e5;
  --series-2: #d95926;
}
.doctor-referral-trend svg:focus-visible { outline: 2px solid var(--text-primary); outline-offset: -2px; }
@media (forced-colors: active) {
  .doctor-referral-trend { --series-1: CanvasText; --series-2: CanvasText; --surface-1: Canvas; --text-primary: CanvasText; --text-secondary: CanvasText; }
}
`;

const VIEW_W = 800;
const VIEW_H = 300;
const PAD = { left: 44, right: 174, top: 24, bottom: 44 };
const PLOT_W = VIEW_W - PAD.left - PAD.right;
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;
const BAND_W = PLOT_W / (MAX_DAYS - 1);
const xOf = (day: number) => PAD.left + ((day - 1) / (MAX_DAYS - 1)) * PLOT_W;

const pathFor = (values: (number | null)[], yScale: (value: number) => number): string => {
  let path = '';
  let pen = false;
  values.forEach((value, index) => {
    if (value === null) {
      pen = false;
      return;
    }
    path += `${pen ? ' L' : ' M'}${xOf(index + 1)} ${yScale(value)}`;
    pen = true;
  });
  return path.trim();
};

const lastPoint = (values: (number | null)[]) => {
  for (let index = values.length - 1; index >= 0; index--) {
    const value = values[index];
    if (value !== null) return { day: index + 1, value };
  }
  return null;
};

interface DoctorReferralTrendProps {
  cases: TrendCaseInput[];
}

const DoctorReferralTrend: React.FC<DoctorReferralTrendProps> = ({ cases }) => {
  const id = useId();
  const [today, setToday] = useState(() => singaporeDateKey(new Date()));
  const [selectedText, setSelectedText] = useState(() => monthInputValue(defaultMonths(today).selected));
  const [comparisonText, setComparisonText] = useState(() => monthInputValue(defaultMonths(today).comparison));
  const [activeDay, setActiveDay] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const refresh = () => setToday(singaporeDateKey(new Date()));
    const timer = window.setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const selectedKey = useMemo(() => parseMonthInput(selectedText), [selectedText]);
  const comparisonKey = useMemo(() => parseMonthInput(comparisonText), [comparisonText]);
  const selectedMessage = monthValidationMessage(selectedKey, today);
  const comparisonMessage = monthValidationMessage(comparisonKey, today);
  const validationMessage = selectedMessage ?? comparisonMessage;
  const trend = useMemo(() => {
    if (!selectedKey || !comparisonKey || validationMessage) return null;
    return buildReferralTrend({ cases, selected: selectedKey, comparison: comparisonKey, today });
  }, [cases, selectedKey, comparisonKey, validationMessage, today]);

  const selectedLabel = selectedKey ? formatMonthLabel(selectedKey) : '';
  const comparisonLabel = comparisonKey ? formatMonthLabel(comparisonKey) : '';
  const maxValue = trend ? Math.max(0, ...trend.days.flatMap((day) => [day.selected ?? 0, day.comparison ?? 0])) : 0;
  const step = Math.max(1, Math.ceil(maxValue / 4));
  const topValue = step * 4;
  const yOf = (value: number) => PAD.top + PLOT_H - (value / topValue) * PLOT_H;
  const yTicks = [0, step, step * 2, step * 3, step * 4];

  const dayText = (series: 'selected' | 'comparison', day: TrendDay): string => {
    const key = series === 'selected' ? selectedKey : comparisonKey;
    if (!key) return '';
    if (day.day > daysInMonth(key)) return 'No such day';
    return day[series] === null ? 'Not yet' : String(day[series]);
  };
  const activeRow = trend && activeDay ? trend.days[activeDay - 1] : null;
  const crosshairX = activeDay ? xOf(activeDay) : 0;
  const selectedEnd = trend ? lastPoint(trend.days.map((day) => day.selected)) : null;
  const comparisonEnd = trend ? lastPoint(trend.days.map((day) => day.comparison)) : null;
  const selectedEndY = selectedEnd ? yOf(selectedEnd.value) - 6 : 0;
  const comparisonEndY = comparisonEnd ? yOf(comparisonEnd.value) - 6 : 0;
  const separatedComparisonY = Math.abs(comparisonEndY - selectedEndY) < 16 ? selectedEndY + 16 : comparisonEndY;

  return (
    <Card className="doctor-referral-trend border-slate-200 shadow-sm" style={{ backgroundColor: 'var(--surface-1)', color: 'var(--text-primary)' }}>
      <style>{TREND_CSS}</style>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Referrals received by day</CardTitle>
        <p className="text-sm text-[var(--text-secondary)]">
          Based on the referral cases loaded here, not complete Plato data. Days are Singapore calendar days.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor={`${id}-selected`} className="text-sm font-medium">Selected month</label>
            <input id={`${id}-selected`} type="month" value={selectedText}
              onChange={(event) => setSelectedText(event.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-[var(--surface-1)] px-2 text-sm"
              aria-invalid={Boolean(selectedMessage)} aria-describedby={selectedMessage ? `${id}-error` : undefined} />
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-comparison`} className="text-sm font-medium">Comparison month</label>
            <input id={`${id}-comparison`} type="month" value={comparisonText}
              onChange={(event) => setComparisonText(event.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-[var(--surface-1)] px-2 text-sm"
              aria-invalid={Boolean(comparisonMessage)} aria-describedby={comparisonMessage ? `${id}-error` : undefined} />
          </div>
        </div>
        {validationMessage && <p id={`${id}-error`} role="alert" className="text-sm text-[var(--text-primary)]">{validationMessage}</p>}
        {trend && (
          <>
            <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--text-secondary)]">
              <li className="flex items-center gap-2">
                <svg width="28" height="8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" stroke="var(--series-1)" strokeWidth="2" /></svg>
                {selectedLabel} (solid)
              </li>
              <li className="flex items-center gap-2">
                <svg width="28" height="8" aria-hidden="true"><line x1="0" y1="4" x2="28" y2="4" stroke="var(--series-2)" strokeWidth="2" strokeDasharray="6 4" /></svg>
                {comparisonLabel} (dashed)
              </li>
            </ul>
            <div className="w-full overflow-x-auto">
              <div className="relative min-w-[640px]">
                <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" role="group" tabIndex={0}
                  aria-label={`Referrals by day: ${selectedLabel} against ${comparisonLabel}. Use left and right arrow keys to inspect each day.`}
                  aria-describedby={`${id}-day`}
                  onPointerLeave={() => setActiveDay(null)} onFocus={() => setActiveDay(1)} onBlur={() => setActiveDay(null)}
                  onKeyDown={(event) => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    setActiveDay((day) => event.key === 'Home' ? 1 : event.key === 'End' ? MAX_DAYS :
                      Math.min(MAX_DAYS, Math.max(1, (day ?? 1) + (event.key === 'ArrowRight' ? 1 : -1))));
                  }}>
                  <text x={PAD.left} y="13" fontSize="11" fill="var(--text-secondary)">Number of referrals</text>
                  {yTicks.map((tick) => (
                    <g key={tick}>
                      <line x1={PAD.left} x2={VIEW_W - PAD.right} y1={yOf(tick)} y2={yOf(tick)} stroke={tick === 0 ? 'var(--baseline)' : 'var(--gridline)'} />
                      <text x={PAD.left - 8} y={yOf(tick)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--text-secondary)">{tick}</text>
                    </g>
                  ))}
                  {[1, 5, 10, 15, 20, 25, 31].map((day) => (
                    <text key={day} x={xOf(day)} y={VIEW_H - 25} textAnchor="middle" fontSize="11" fill="var(--text-secondary)">{day}</text>
                  ))}
                  <text x={PAD.left + PLOT_W / 2} y={VIEW_H - 5} textAnchor="middle" fontSize="11" fill="var(--text-secondary)">Day of month</text>
                  <path d={pathFor(trend.days.map((day) => day.comparison), yOf)} fill="none" stroke="var(--series-2)" strokeWidth="2" strokeDasharray="6 4" />
                  <path d={pathFor(trend.days.map((day) => day.selected), yOf)} fill="none" stroke="var(--series-1)" strokeWidth="2" />
                  {selectedEnd && (
                    <g>
                      <circle cx={xOf(selectedEnd.day)} cy={yOf(selectedEnd.value)} r="4" fill="var(--series-1)" stroke="var(--surface-1)" strokeWidth="2" />
                      <text x={VIEW_W - PAD.right + 12} y={selectedEndY} fontSize="11" fill="var(--text-primary)">{selectedLabel}: {selectedEnd.value}</text>
                    </g>
                  )}
                  {comparisonEnd && (
                    <g>
                      <circle cx={xOf(comparisonEnd.day)} cy={yOf(comparisonEnd.value)} r="4" fill="var(--series-2)" stroke="var(--surface-1)" strokeWidth="2" />
                      <text x={VIEW_W - PAD.right + 12} y={separatedComparisonY} fontSize="11" fill="var(--text-primary)">{comparisonLabel}: {comparisonEnd.value}</text>
                    </g>
                  )}
                  {activeDay && <line x1={crosshairX} x2={crosshairX} y1={PAD.top} y2={PAD.top + PLOT_H} stroke="var(--text-secondary)" />}
                  {trend.days.map((day) => (
                    <rect key={day.day} x={xOf(day.day) - BAND_W / 2} y={PAD.top} width={BAND_W} height={PLOT_H}
                      fill="transparent" onPointerEnter={() => setActiveDay(day.day)} />
                  ))}
                </svg>
                {activeRow && activeDay && (
                  <div className="pointer-events-none absolute top-2 -translate-x-1/2 rounded-md border border-slate-200 bg-[var(--surface-1)] px-2 py-1 text-xs text-[var(--text-primary)] shadow"
                    style={{ left: `${Math.min(82, Math.max(18, (crosshairX / VIEW_W) * 100))}%` }}>
                    <p className="font-semibold">Day {activeDay}</p>
                    <p>{selectedLabel}: {dayText('selected', activeRow)}</p>
                    <p>{comparisonLabel}: {dayText('comparison', activeRow)}</p>
                  </div>
                )}
              </div>
            </div>
            <p id={`${id}-day`} aria-live="polite" className="sr-only">
              {activeRow ? `Day ${activeDay}. ${selectedLabel}: ${dayText('selected', activeRow)}. ${comparisonLabel}: ${dayText('comparison', activeRow)}.` : 'Focus the chart to inspect daily counts.'}
            </p>
            <div className="flex flex-wrap gap-x-4 text-xs text-[var(--text-secondary)]">
              <span>Unique cases loaded: {trend.loadedCases}</span>
              <span>Duplicate loaded cases counted once: {trend.duplicateCases}</span>
              <span>Excluded, missing or invalid referral date: {trend.excludedDateCases}</span>
              {trend.invalidIdCases > 0 && <span>Excluded, no usable ID: {trend.invalidIdCases}</span>}
            </div>
            <button type="button" className="text-sm font-medium text-[var(--text-primary)] underline"
              aria-expanded={detailsOpen} aria-controls={`${id}-table`} onClick={() => setDetailsOpen((open) => !open)}>
              {detailsOpen ? 'Hide day-by-day table' : 'Show day-by-day table'}
            </button>
            {detailsOpen && (
              <div id={`${id}-table`} className="max-h-72 overflow-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">Referrals received by day</caption>
                  <thead><tr><th scope="col" className="text-left">Day</th><th scope="col" className="text-right">{selectedLabel}</th><th scope="col" className="text-right">{comparisonLabel}</th></tr></thead>
                  <tbody>{trend.days.map((day) => (
                    <tr key={day.day}><th scope="row" className="text-left font-normal">{day.day}</th><td className="text-right">{dayText('selected', day)}</td><td className="text-right">{dayText('comparison', day)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DoctorReferralTrend;
